import React, { useState, useEffect } from 'react';

interface Feedback {
  id: number;
  user_id: number;
  user_email: string;
  user_name: string;
  type: string;
  severity: string;
  status: string;
  title: string;
  description: string;
  page_url: string;
  section_number: string;
  question_number: string;
  protocol_id: number;
  created_at: string;
}

interface FeedbackDashboardProps {
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
  onBack: () => void;
}

export default function FeedbackDashboard({
  apiBaseUrl,
  getAuthHeaders,
  onBack
}: FeedbackDashboardProps) {
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    status: ''
  });

  useEffect(() => {
    loadFeedback();
  }, [filters]);

  const loadFeedback = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.severity) queryParams.append('severity', filters.severity);
      if (filters.status) queryParams.append('status', filters.status);

      const response = await fetch(
        `${apiBaseUrl}/api/feedback?${queryParams}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load feedback');
      const data = await response.json();
      setFeedbackList(data);
    } catch (error) {
      console.error('Error loading feedback:', error);
      alert('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (id: number, newStatus: string, comment?: string) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/feedback/${id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ status: newStatus, comment })
        }
      );

      if (!response.ok) throw new Error('Failed to update status');

      // Reload feedback list
      await loadFeedback();
      alert('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return '🔴';
      case 'major': return '🟡';
      case 'minor': return '🟢';
      default: return '';
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      new: '#3b82f6',
      reviewing: '#f59e0b',
      resolved: '#10b981',
      closed: '#6b7280',
      wont_fix: '#ef4444'
    };
    return (
      <span style={{
        backgroundColor: colors[status] || '#999',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        textTransform: 'uppercase'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="feedback-dashboard">
      <div className="dashboard-header">
        <button onClick={onBack} className="secondary-button">
          ← Back
        </button>
        <h1>Feedback Dashboard</h1>
      </div>

      {/* Summary Stats */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-value">{feedbackList.filter(f => f.status === 'new').length}</div>
          <div className="stat-label">New</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{feedbackList.filter(f => f.status === 'reviewing').length}</div>
          <div className="stat-label">Reviewing</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{feedbackList.filter(f => f.status === 'resolved').length}</div>
          <div className="stat-label">Resolved</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{feedbackList.length}</div>
          <div className="stat-label">Total</div>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <select
          value={filters.type}
          onChange={(e) => setFilters({...filters, type: e.target.value})}
          className="form-control"
        >
          <option value="">All Types</option>
          <option value="bug">Bug</option>
          <option value="feature">Feature</option>
          <option value="other">Other</option>
        </select>

        <select
          value={filters.severity}
          onChange={(e) => setFilters({...filters, severity: e.target.value})}
          className="form-control"
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="major">Major</option>
          <option value="minor">Minor</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) => setFilters({...filters, status: e.target.value})}
          className="form-control"
        >
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="reviewing">Reviewing</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Feedback List */}
      {isLoading ? (
        <div className="loading">Loading feedback...</div>
      ) : feedbackList.length === 0 ? (
        <div className="empty-state">
          <p>No feedback found.</p>
        </div>
      ) : (
        <div className="feedback-list">
          {feedbackList.map(item => (
            <div key={item.id} className="feedback-item">
              <div className="feedback-header">
                <div className="feedback-meta">
                  {getSeverityIcon(item.severity)}
                  <strong>{item.type.toUpperCase()}</strong>
                  {getStatusBadge(item.status)}
                </div>
                <div className="feedback-date">
                  {new Date(item.created_at).toLocaleDateString()}
                </div>
              </div>

              <h3 className="feedback-title">{item.title}</h3>

              <p className="feedback-description">{item.description}</p>

              {/* Context info */}
              {(item.section_number || item.question_number || item.protocol_id) && (
                <div className="feedback-context">
                  {item.section_number && <span>Section {item.section_number}</span>}
                  {item.question_number && <span>Question {item.question_number}</span>}
                  {item.protocol_id && <span>Protocol #{item.protocol_id}</span>}
                </div>
              )}

              <div className="feedback-footer">
                <div className="feedback-user">
                  By: {item.user_name || item.user_email}
                </div>

                <div className="feedback-actions">
                  {item.status === 'new' && (
                    <button
                      onClick={() => updateStatus(item.id, 'reviewing')}
                      className="link-button"
                    >
                      Start Reviewing
                    </button>
                  )}
                  {item.status === 'reviewing' && (
                    <button
                      onClick={() => updateStatus(item.id, 'resolved')}
                      className="link-button"
                    >
                      Mark Resolved
                    </button>
                  )}
                  {item.status === 'resolved' && (
                    <button
                      onClick={() => updateStatus(item.id, 'closed')}
                      className="link-button"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
